<script setup lang="ts">
import { SectionApplianceIcon, type IVirtualizedTreeNode } from "@voltalis/ui-kit/components";
import { cloneDeep } from "es-toolkit";
import { computed, ref, watch } from "vue";

import { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import { EWizardCloseState } from "@/components/ui/wizard/wizard.types";
import { useSiteSectionAppliancesV2Query } from "@/composables/kili/sections/queries/use-get-section-appliances-v2-query";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { SiteSectionId, V4HSiteId } from "@/data/common/ids";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import { ProgrammableAppliance, SectionNode } from "@/domains/v4b/site-section/site-section.types";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Dialog from "primevue/dialog";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";

import SiteSectionsTreeApplianceCategoryFilter from "@/app/tools/new-section-management/site-sections-tree/filters/appliance-category-filter/site-sections-tree-appliance-category-filter.vue";
import MoveSectionAppliancesWizard from "@/app/tools/new-section-management/site-sections-tree/section-appliances/move-section-appliances-wizard/move-section-appliances-wizard.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  siteId: V4HSiteId;
  section: SectionNode;
  siteSectionsTree?: IVirtualizedTreeNode<TSiteSectionsTreeData>;
}

const props = defineProps<IProps>();
const visible = defineModel<boolean>({
  required: true,
});

const currentRouteName = computed(() => router.currentRoute.value.name);

const { t, k } = useTypedIntl();

const selectedAppliances = ref<ProgrammableAppliance[]>([]);
const searchQuery = ref<string>("");
const isMoveSectionAppliancesWizardVisible = ref<boolean>(false);
const selectedApplianceCategories = ref<EProgramApplianceCategory[]>([]);

const computedSiteId = computed<V4HSiteId>(() => props.siteId);
const computedSectionId = computed<SiteSectionId>(() => props.section.id);

const { data: sectionAppliances, isLoading: isFetchingSectionAppliances } = useSiteSectionAppliancesV2Query(
  computedSiteId,
  computedSectionId,
);

function filterApplianceByName(query: string, appliance: ProgrammableAppliance): boolean {
  return appliance.name.toLowerCase().includes(query.toLowerCase());
}

function filterApplianceById(query: string, appliance: ProgrammableAppliance): boolean {
  return appliance.applianceId.toString().includes(query);
}

const filteredAppliances = computed<ProgrammableAppliance[]>(() => {
  if (!sectionAppliances.value) return [];

  if (!searchQuery.value && !selectedApplianceCategories.value.length) {
    return sectionAppliances.value;
  }

  const searchQueryResult = sectionAppliances.value.filter(
    (appliance) =>
      filterApplianceByName(searchQuery.value, appliance) || filterApplianceById(searchQuery.value, appliance),
  );

  if (selectedApplianceCategories.value.length) {
    return searchQueryResult.filter((appliance) => selectedApplianceCategories.value.includes(appliance.category));
  }

  return searchQueryResult;
});

function selectAll() {
  selectedAppliances.value = cloneDeep(filteredAppliances.value);
}

function unselectAll() {
  selectedAppliances.value = [];
}

function resetFilters() {
  searchQuery.value = "";
  selectedApplianceCategories.value = [];
}

function handleOpenMoveSectionAppliancesWizard() {
  isMoveSectionAppliancesWizardVisible.value = true;
  visible.value = false;
}

function handleCloseMoveSectionAppliancesWizard(state: EWizardCloseState) {
  if (state === EWizardCloseState.SUCCESS) {
    resetFilters();
    return;
  }

  visible.value = true;
}

function resetSelectedAppliances() {
  selectedAppliances.value = [];
}

function handleSelectedApplianceCategoriesChanged(newSelectedApplianceCategoryIds: EProgramApplianceCategory[]) {
  if (newSelectedApplianceCategoryIds.length) {
    MixpanelService.trackEvent(EMixpanelEvent.APPLIANCE_TYPE_FILTER_APPLIED, {
      category_id: newSelectedApplianceCategoryIds,
      page_context: currentRouteName.value?.toString(),
    });
  } else {
    MixpanelService.trackEvent(EMixpanelEvent.APPLIANCE_TYPE_FILTER_CLEARED, {
      page_context: currentRouteName.value?.toString(),
      method: "dropdown",
    });
  }
}

watch(visible, (newValue) => {
  if (!newValue) return;

  MixpanelService.trackEvent(EMixpanelEvent.SECTION_APPLIANCE_LIST_VIEWED);
});
</script>

<template>
  <Dialog
    v-model:visible="visible"
    class="section-appliances-dialog w-[860px]"
    modal
    :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.component"
  >
    <template #header>
      <div class="flex flex-col gap-2">
        <div class="flex items-center row-dot-divider">
          <h3
            class="font-semibold"
            :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.title"
          >
            {{ t(k.tools.siteManagement.sectionAppliances.title) }}
          </h3>
          <h3
            class="font-semibold"
            :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.sectionName"
          >
            {{ props.section.name }}
          </h3>
        </div>
        <span
          class="text-text-secondary !text-sm"
          :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.applianceCountText"
          >{{
            t(k.tools.siteManagement.sectionAppliances.applianceCount({ count: sectionAppliances?.length ?? 0 }))
          }}</span
        >
      </div>
    </template>

    <DataTable
      :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.appliancesTable.table"
      class="section-appliance-table"
      v-model:selection="selectedAppliances"
      :value="filteredAppliances"
      scrollable
      scrollHeight="500px"
      :virtualScrollerOptions="{ itemSize: 50 }"
      tableStyle="min-width: 50rem"
      :loading="isFetchingSectionAppliances"
      dataKey="applianceId"
    >
      <template #empty>
        <div class="flex w-full h-full justify-center items-center">
          <span
            class="text-gray-500"
            :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.appliancesTable.noResultText"
          >
            {{ t(k.global.misc.noResult) }}
          </span>
        </div>
      </template>

      <template #header>
        <div class="w-full flex flex-row py-4 px-6 gap-4">
          <IconField
            class="flex flex-1"
            iconPosition="left"
          >
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>
            <InputText
              v-model="searchQuery"
              class="!text-sm"
              :data-test-id="
                DATA_TEST_IDS.components.sectionAppliancesV2Dialog.appliancesTable.searchApplianceInputText
              "
              :placeholder="t(k.tools.siteManagement.sectionAppliances.table.searchApplianceByNameOrId)"
            />
          </IconField>

          <SiteSectionsTreeApplianceCategoryFilter
            v-model:selected-categories="selectedApplianceCategories"
            :site-sections-tree="props.siteSectionsTree"
            @update:selected-categories="handleSelectedApplianceCategoriesChanged"
          />
        </div>
      </template>

      <Column
        selectionMode="multiple"
        headerStyle="width: 50px"
      />
      <Column
        field="category"
        headerStyle="width: 50px"
      >
        <template #body="slotProps: { data: ProgrammableAppliance }">
          <SectionApplianceIcon
            v-tooltip.top="t(k.global.applianceCategory[slotProps.data.category])"
            :section-appliance-category="slotProps.data.category"
            size="large"
            :data-test-appliance-id="slotProps.data.applianceId"
          />
        </template>
      </Column>
      <Column
        class="!w-[400px]"
        field="name"
        :header="t(k.tools.siteManagement.sectionAppliances.table.applianceName)"
      >
        <template #body="slotProps: { data: ProgrammableAppliance }">
          <span
            class="font-semibold truncate whitespace-nowrap text-ellipsis"
            :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.appliancesTable.applianceName"
            :data-test-appliance-id="slotProps.data.applianceId"
            >{{ slotProps.data.name }}</span
          >
        </template>
      </Column>
      <Column
        field="id"
        :header="t(k.tools.siteManagement.sectionAppliances.table.applianceId)"
      >
        <template #body="slotProps: { data: ProgrammableAppliance }">
          <span
            class="uppercase text-gray-500"
            :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.appliancesTable.applianceId"
            :data-test-appliance-id="slotProps.data.applianceId"
            >{{ slotProps.data.applianceId }}</span
          >
        </template>
      </Column>
    </DataTable>

    <template #footer>
      <div class="flex flex-row justify-between w-full items-center">
        <HospitalityButton
          v-if="filteredAppliances.length === selectedAppliances.length"
          :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.appliancesTable.unselectAllButton"
          :label="t(k.global.misc.unselectAll)"
          severity="secondary"
          @click="unselectAll"
        />
        <HospitalityButton
          v-else
          :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.appliancesTable.selectAllButton"
          :label="t(k.global.misc.selectAll)"
          severity="secondary"
          @click="selectAll"
        />
        <HospitalityButton
          :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.moveToButton.buton"
          :disabled="!selectedAppliances.length"
          @click="handleOpenMoveSectionAppliancesWizard"
        >
          <i
            class="not-italic bg-white w-fit rounded-full text-brand py-0 px-[6px] text-xs mr-2"
            :data-test-id="DATA_TEST_IDS.components.sectionAppliancesV2Dialog.moveToButton.selectCount"
          >
            {{ selectedAppliances.length }}
          </i>
          {{ t(k.tools.siteManagement.sectionAppliances.table.moveTo) }}
        </HospitalityButton>
      </div>
    </template>
  </Dialog>

  <MoveSectionAppliancesWizard
    v-model:visible="isMoveSectionAppliancesWizardVisible"
    :selectedAppliances="selectedAppliances"
    :site-id="props.siteId"
    :initial-parent="props.section"
    :site-sections-tree="props.siteSectionsTree"
    @appliances-moved="resetSelectedAppliances"
    @close="handleCloseMoveSectionAppliancesWizard"
  />
</template>

<style lang="scss">
.section-appliances-dialog {
  @apply gap-0 !important;

  .p-dialog-header {
    border-bottom: 1px solid !important;
    @apply border-gray-200 !important;
  }

  .p-dialog-content {
    @apply p-0 !important;
  }

  .p-dialog-footer {
    border-top: 1px solid !important;
    @apply p-4 border-gray-200 !important;
  }

  .section-appliance-table {
    .p-datatable-table-container {
      @apply border-0 rounded-t-none !important;
    }

    table {
      @apply border-separate !important;

      thead {
        th {
          @apply bg-gray-100 !important;
        }

        .p-datatable-header-cell {
          @apply h-[48px] !important;
        }

        .p-datatable-column-title {
          @apply text-xs uppercase text-text-secondary font-normal !important;
        }
      }

      tr > td {
        @apply py-0 h-[50px] !important;

        &:first-child {
          @apply pl-6 pr-4 !important;
        }

        &:last-child {
          @apply pl-0 pr-6 !important;
        }

        &:not(:first-child):not(:last-child) {
          @apply pl-0 pr-4 !important;
        }
      }

      tbody > tr:not(:last-child) > td {
        @apply border-b !important;
      }
    }
  }
}
</style>
